import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Dialog,
  Divider,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { MasterSchemeInterface } from "../../../schemes/family_enquiries/family_enquiry_list";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { Option } from "../../../needs/model/needs_filters";
import { useQuery } from "react-query";
import { getDistrictMasterData } from "../../../../services/master/master_service";
import {
  FilterDialogFooter,
  FilterDialogHeader,
  PillToggleButton,
} from "../filter_dialog/filter_dialog_parts";
import { black, purple, purpleLight } from "../../../../theme/colors";

interface FilterEnquiriesInterface {
  openModal: boolean;
  setOpenModal: (val: boolean) => void;
  startDate: string;
  setStartDate: (value: string) => void;
  endDate: string;
  setEndDate: (value: string) => void;
  schemeKeyword: string;
  setSchemeKeyword: (value: string) => void;
  states: MasterSchemeInterface[];
  category: MasterSchemeInterface[];
  education: MasterSchemeInterface[];
  income: MasterSchemeInterface[];
  occupation: MasterSchemeInterface[];
  stateId: number | null;
  setStateId: (val: number | null) => void;
  categoryId: number | null;
  setCategoryId: (val: number | null) => void;
  resetFilter: () => void;
  genderList: MasterSchemeInterface[];
  selectedGender: number | null;
  setSelectedGender: (val: number | null) => void;
  setAgeFrom: (value: number | null) => void;
  setAgeTo: (value: number | null) => void;
  setEducationId: (value: number | null) => void;
  setIncomeId: (value: number | null) => void;
  setOccupationId: (value: number | null) => void;
  setPage: (val: number) => void;
  isSubscribed: boolean;
  setIsSubscribed: (val: boolean) => void;
  cities: Option[] | [];
  cityId: number | null;
  setCityId: (value: number | null) => void;
  setDistrictId: (value: number | null) => void;
  districtId: null | number;
}

export default function FilterUsers({
  openModal,
  setOpenModal,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  schemeKeyword,
  setSchemeKeyword,
  states,
  category,
  stateId,
  setStateId,
  categoryId,
  setCategoryId,
  resetFilter,
  genderList,
  selectedGender,
  setSelectedGender,
  education,
  income,
  setAgeFrom,
  setAgeTo,
  setEducationId,
  setIncomeId,
  setOccupationId,
  occupation,
  setPage,
  isSubscribed,
  setIsSubscribed,
  cities,
  setCityId,
  cityId,
  setDistrictId
}: FilterEnquiriesInterface) {
  const [_endDate, _setEndDate] = useState<string>(endDate);
  const [_startDate, _setStartDate] = useState<string>(endDate);
  const [_schemeKeyword, _setSchemeKeyword] = useState("");
  const [_stateId, _setStateId] = useState<number | null>(null);
  const [_categoryId, _setCategoryId] = useState<number | null>(null);
  const [_ageFrom, _setAgeFrom] = useState<number | null>(null);
  const [_ageTo, _setAgeTo] = useState<number | null>(null);
  const [_selectedGender, _setSelectedGender] = useState<number | null>(null);
  const [_educationId, _setEducationId] = useState<number | null>(null);
  const [_incomeId, _setIncomeId] = useState<number | null>(null);
  const [_occupationId, _setOccupationId] = useState<number | null>(null);
  const [_isSubscribed, _setIsSubscribed] = useState<boolean>(false);
  const [_cityId, _setCityId] = useState<number | null>(cityId);
  const [_districtId, _setDistrictId] = useState<number | null>(null);

  useEffect(() => {
    if (_endDate != null && _startDate != null && _startDate > _endDate) {
      _setEndDate("");
    }
  }, [_startDate]);

  const { data: district } = useQuery(["districtList", _stateId], async () => {
    if (_stateId) {
      let response = await getDistrictMasterData(_stateId);
      return response?.data?.data?.list;
    }
  });

  function _dateRange({
    title,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
  }: {
    title: string;
    startDate: string;
    setStartDate: (value: string) => void;
    endDate: string;
    setEndDate: (value: string) => void;
  }) {
    return (
      <Box pt={2} pb={1}>
        <Typography fontSize={14} fontWeight="700" color={black}>
          {title}
        </Typography>
        <Box pt={1} pb={2} display="flex" flexDirection={"column"}>
          <DatePicker
            label="Start Date"
            format="DD-MM-YYYY"
            disableFuture
            value={startDate ? dayjs(startDate, "YYYY-MM-DD") : null}
            onChange={(value: Dayjs | null) => {
              setStartDate(value?.format("YYYY-MM-DD") || "");
            }}
            slotProps={{ desktopPaper: { elevation: 3 } }}
            sx={{
              "& .MuiSvgIcon-root": { fill: purple },
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
            }}
          />
          <Box p={1}></Box>
          <DatePicker
            label="End Date"
            format="DD-MM-YYYY"
            disableFuture
            value={endDate ? dayjs(endDate, "YYYY-MM-DD") : null}
            onChange={(value: Dayjs | null) => {
              setEndDate(value?.format("YYYY-MM-DD") || "");
            }}
            slotProps={{ desktopPaper: { elevation: 3 } }}
            sx={{
              "& .MuiSvgIcon-root": { fill: purple },
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
            }}
          />
        </Box>
      </Box>
    );
  }


  function resetAll() {
    _setStartDate("");
    _setEndDate("");
    _setCategoryId(null);
    _setStateId(null);
    _setAgeFrom(null);
    _setAgeTo(null);
    _setEducationId(null);
    _setIncomeId(null);
    _setOccupationId(null);
    _setSelectedGender(null);
    _setIsSubscribed(false);
    _setCityId(null);
    resetFilter();
    setOpenModal(false);
    _setDistrictId(null);
  }

  return (
    <Dialog open={openModal} PaperProps={{ sx: { borderRadius: 3 } }}>
      <Box sx={{ minWidth: 420, maxWidth: "90vw" }}>
        <Card elevation={0} sx={{ px: 1 }}>
          <FilterDialogHeader
            title="Filter Users"
            onClose={() => setOpenModal(false)}
            onReset={resetAll}
          />
          <CardContent sx={{ maxHeight: 420, overflow: "auto scroll" }}>
            <Box
              display="flex"
              flexDirection={"row"}
              alignItems={"center"}
              justifyContent="space-between"
              sx={{ bgcolor: purpleLight, borderRadius: 2, px: 2, py: 1 }}
            >
              <Typography fontSize={14} fontWeight="600" color={black}>
                Subscribed users only
              </Typography>
              <Switch
                checked={_isSubscribed}
                sx={{ m: 0 }}
                onChange={() => _setIsSubscribed(!_isSubscribed)}
              />
            </Box>
            <Divider sx={{ mt: 2, mb: 1 }} />
            <Typography fontSize={14} fontWeight="700" color={black} mb={1}>
              Gender
            </Typography>
            <Box>
              {genderList?.map((gender) => (
                <PillToggleButton
                  key={gender.id}
                  label={gender.value}
                  active={_selectedGender === gender.id}
                  onClick={() => {
                    if (_selectedGender === gender?.id) {
                      _setSelectedGender(null);
                    } else {
                      _setSelectedGender(gender?.id);
                    }
                  }}
                />
              ))}
            </Box>
            {_dropdownOption({
              title: "State",
              placeholder: "Search for State",
              options: states,
              selectedId: _stateId,
              onSelect: (id) => {
                _setStateId(id);
              },
            })}
            {_dropdownOption({
              title: "District",
              placeholder: "Select District",
              selectedId: _districtId,
              onSelect: (id) => {
                _setDistrictId(id);
              },
              options:
                district?.map((city: any) => {
                  return { id: city.id, value: city?.value };
                }) || [],
            })}
            {/* {_dropdownOption({
              title: "City",
              placeholder: "Select City",
              selectedId: _cityId,
              onSelect: (id) => {
                _setCityId(id);
              },
              options:
                cities.map((city) => {
                  return { id: city._id, value: city.name };
                }) || [],
            })} */}

            {_dropdownOption({
              title: "Occupation",
              placeholder: "Search for Occupation",
              options: occupation,
              selectedId: _occupationId,
              onSelect: (id) => {
                _setOccupationId(id);
              },
            })}
            {_dropdownOption({
              title: "Education",
              placeholder: "Search for Education",
              options: education,
              selectedId: _educationId,
              onSelect: (id) => {
                _setEducationId(id);
              },
            })}
            {_dropdownOption({
              title: "Current Income",
              placeholder: "Search for Current Income",
              options: income,
              selectedId: _incomeId,
              onSelect: (id) => {
                _setIncomeId(id);
              },
            })}

            {_dateRange({
              title: "Date Range",
              setEndDate: _setEndDate,
              setStartDate: _setStartDate,
              startDate: _startDate,
              endDate: _endDate,
            })}

            <FilterDialogFooter
              onCancel={() => setOpenModal(false)}
              onApply={() => {
                setStartDate(_startDate);
                setEndDate(_endDate);
                setSchemeKeyword(_schemeKeyword);
                setCategoryId(_categoryId);
                setStateId(_stateId);
                setOpenModal(false);
                setAgeFrom(_ageFrom);
                setAgeTo(_ageTo);
                setEducationId(_educationId);
                setIncomeId(_incomeId);
                setOccupationId(_occupationId);
                setSelectedGender(_selectedGender);
                setPage(0);
                setIsSubscribed(_isSubscribed);
                setCityId(_cityId);
                setDistrictId(_districtId);
              }}
            />
          </CardContent>
        </Card>
      </Box>
    </Dialog>
  );

  function _dropdownOption({
    title,
    placeholder,
    options,
    selectedId,
    onSelect,
  }: {
    title: string;
    placeholder: string;
    options: { id: number; value: string }[];
    selectedId: number | null;
    onSelect: (id: number | null) => void;
  }) {
    return (
      <Box pt={2} pb={1}>
        <Typography fontSize={14} fontWeight="700" color={black}>
          {title}
        </Typography>
        <Box pt={1} pb={2} display="flex" flexDirection="row">
          <Autocomplete
            fullWidth
            value={options.find((option) => selectedId === option?.id)}
            onChange={(e, value) => {
              onSelect(value?.id || null);
            }}
            options={options}
            getOptionLabel={(option) => option?.value}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label={placeholder}
                placeholder={placeholder}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            )}
          />
        </Box>
        <Divider />
      </Box>
    );
  }
}
